import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrashAlt, faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';

const Settings = () => {
  const [activeTab, setActiveTab] = useState('currency_units');
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  
  // Dynamic configuration state
  const [coatingSystems, setCoatingSystems] = useState([]);
  const [degradationParams, setDegradationParams] = useState([]);
  const [showCoatingModal, setShowCoatingModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [coatingFormData, setCoatingFormData] = useState({
    system_code: '',
    system_name: '',
    description: '',
    coat_sequence: ['prime', 'topcoat'],
    is_active: true
  });

  // Currency Conversion Rates state
  const [conversionRates, setConversionRates] = useState([]);
  const [conversionRatesLoading, setConversionRatesLoading] = useState(false);
  const [showConversionRateModal, setShowConversionRateModal] = useState(false);
  const [editingConversionRate, setEditingConversionRate] = useState(null);
  const [conversionRateFormData, setConversionRateFormData] = useState({
    year: new Date().getFullYear(),
    from_currency: 'USD',
    to_currency: 'CAD',
    conversion_rate: '',
    is_active: true
  });

  const tabs = [
    { id: 'currency_units', name: 'Currency & Units', icon: 'faDollarSign' },
    { id: 'project_defaults', name: 'Project Defaults', icon: 'faProjectDiagram' }
  ];

  useEffect(() => {
    fetchSettings();
    if (activeTab === 'currency_units') {
      fetchConversionRates();
    }
  }, [activeTab]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/categories');
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage('Error loading settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCoatingSystems = async () => {
    try {
      const response = await api.get('/dynamic-config/coating-systems');
      if (response.data.success) {
        setCoatingSystems(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching coating systems:', error);
      setMessage('Error loading coating systems');
    }
  };

  const fetchDegradationParams = async () => {
    try {
      const response = await api.get('/dynamic-config/degradation-parameters');
      if (response.data.success) {
        setDegradationParams(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching degradation parameters:', error);
      setMessage('Error loading degradation parameters');
    }
  };

  const fetchConversionRates = async () => {
    try {
      setConversionRatesLoading(true);
      const response = await api.get('/currency-conversion-rates');
      if (response.data.success) {
        setConversionRates(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching conversion rates:', error);
      setMessage('Error loading currency conversion rates');
    } finally {
      setConversionRatesLoading(false);
    }
  };

  const saveSettings = async (categoryData) => {
    try {
      setSaving(true);
      const response = await api.put(`/settings/categories/${activeTab}`, categoryData);
      if (response.data.success) {
        setMessage('Settings saved successfully');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage('Error saving settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [key]: {
          ...prev[activeTab][key],
          value: value
        }
      }
    }));
  };

  const handleSave = () => {
    const categoryData = {};
    Object.keys(settings[activeTab] || {}).forEach(key => {
      categoryData[key] = settings[activeTab][key].value;
    });
    saveSettings(categoryData);
  };

  const renderSettingField = (key, settingData) => {
    if (!settingData || !settingData.isEditable) {
      return null;
    }

    const { value, type, description } = settingData;
    
    const renderInput = () => {
      switch (type) {
        case 'boolean':
          return (
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={value === true || value === 'true'}
                onChange={(e) => handleInputChange(key, e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm text-gray-700">
                {value === true || value === 'true' ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          );
        
        case 'number':
          return (
            <input
              type="number"
              step={key.includes('percentage') ? '0.1' : '1'}
              value={value || ''}
              onChange={(e) => handleInputChange(key, parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          );
        
        case 'json':
          return (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Asymptote</label>
                  <input
                    type="number"
                    step="0.01"
                    value={value?.asymptote || ''}
                    onChange={(e) => {
                      const newValue = { ...value, asymptote: parseFloat(e.target.value) || 0 };
                      handleInputChange(key, newValue);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    value={value?.rate || ''}
                    onChange={(e) => {
                      const newValue = { ...value, rate: parseFloat(e.target.value) || 0 };
                      handleInputChange(key, newValue);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>
          );
        
        default: // string
          if (key.includes('currency')) {
            return (
              <select
                value={value || ''}
                onChange={(e) => handleInputChange(key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="CAD">CAD</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
              </select>
            );
          }
          
          if (key.includes('unit')) {
            return (
              <select
                value={value || ''}
                onChange={(e) => handleInputChange(key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="m²">m² (Square Meters)</option>
                <option value="ft²">ft² (Square Feet)</option>
              </select>
            );
          }

          if (key.includes('mobilization_method')) {
            return (
              <select
                value={value || ''}
                onChange={(e) => handleInputChange(key, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="weekly_scaffolding">Weekly Scaffolding</option>
                <option value="fixed_cost">Fixed Cost</option>
              </select>
            );
          }
          
          return (
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleInputChange(key, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          );
      }
    };

    return (
      <div key={key} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-900 mb-2">
              {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </label>
            {description && (
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                {description}
              </p>
            )}
            {renderInput()}
          </div>
          {!settingData.isEditable && (
            <div className="ml-4 flex-shrink-0">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                <i className="fas fa-lock mr-1"></i>
                Read Only
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Dynamic configuration CRUD handlers
  const handleDeleteCoatingSystem = async (id) => {
    if (window.confirm('Are you sure you want to delete this coating system?')) {
      try {
        await api.delete(`/dynamic-config/coating-systems/${id}`);
        fetchCoatingSystems();
        setMessage('Coating system deleted successfully');
      } catch (error) {
        console.error('Error deleting coating system:', error);
        setMessage('Error deleting coating system');
      }
    }
  };

  const handleEditDegradationParam = async (param) => {
    // Use a more user-friendly approach
    const newCoeff = window.prompt(`Edit Coefficient A for ${param.corrosion_category}:`, param.coefficient_a);
    if (newCoeff === null) return; // User cancelled
    
    const newRate = window.prompt(`Edit Growth Rate B for ${param.corrosion_category}:`, param.growth_rate_b);
    if (newRate === null) return; // User cancelled
    
    if (newCoeff && newRate && !isNaN(parseFloat(newCoeff)) && !isNaN(parseFloat(newRate))) {
      try {
        await api.put(`/dynamic-config/degradation-parameters/${param.id}`, {
          coefficient_a: parseFloat(newCoeff),
          growth_rate_b: parseFloat(newRate)
        });
        fetchDegradationParams();
        setMessage(`Degradation parameters for ${param.corrosion_category} updated successfully`);
      } catch (error) {
        console.error('Error updating degradation parameter:', error);
        setMessage('Error updating degradation parameter');
      }
    } else {
      setMessage('Invalid input. Please enter valid numbers.');
    }
  };

  const handleSaveCoatingSystem = async () => {
    try {
      if (editingItem) {
        await api.put(`/dynamic-config/coating-systems/${editingItem.id}`, coatingFormData);
      } else {
        await api.post('/dynamic-config/coating-systems', coatingFormData);
      }
      setShowCoatingModal(false);
      setEditingItem(null);
      fetchCoatingSystems();
      setMessage('Coating system saved successfully');
    } catch (error) {
      console.error('Error saving coating system:', error);
      setMessage('Error saving coating system');
    }
  };

  // Currency Conversion Rates handlers
  const handleSaveConversionRate = async () => {
    try {
      if (editingConversionRate) {
        await api.put(`/currency-conversion-rates/${editingConversionRate.id}`, conversionRateFormData);
        setMessage('Conversion rate updated successfully');
      } else {
        await api.post('/currency-conversion-rates', conversionRateFormData);
        setMessage('Conversion rate created successfully');
      }
      setShowConversionRateModal(false);
      setEditingConversionRate(null);
      setConversionRateFormData({
        year: new Date().getFullYear(),
        from_currency: 'USD',
        to_currency: 'CAD',
        conversion_rate: '',
        is_active: true
      });
      fetchConversionRates();
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving conversion rate:', error);
      setMessage(error.response?.data?.message || 'Error saving conversion rate');
    }
  };

  const handleDeleteConversionRate = async (id) => {
    if (window.confirm('Are you sure you want to delete this conversion rate?')) {
      try {
        await api.delete(`/currency-conversion-rates/${id}`);
        setMessage('Conversion rate deleted successfully');
        fetchConversionRates();
        setTimeout(() => setMessage(''), 3000);
      } catch (error) {
        console.error('Error deleting conversion rate:', error);
        setMessage('Error deleting conversion rate');
      }
    }
  };

  const handleEditConversionRate = (rate) => {
    setEditingConversionRate(rate);
    setConversionRateFormData({
      year: rate.year,
      from_currency: rate.fromCurrency,
      to_currency: rate.toCurrency,
      conversion_rate: rate.conversionRate,
      is_active: rate.isActive
    });
    setShowConversionRateModal(true);
  };

  const handleAddConversionRate = () => {
    setEditingConversionRate(null);
    setConversionRateFormData({
      year: new Date().getFullYear(),
      from_currency: 'USD',
      to_currency: 'CAD',
      conversion_rate: '',
      is_active: true
    });
    setShowConversionRateModal(true);
  };

  // Currency & Units tab renderer with Conversion Rates
  const renderCurrencyUnitsTab = () => {
    const categorySettings = settings['currency_units'] || {};
    const settingsKeys = Object.keys(categorySettings);

    return (
      <div className="space-y-6">
        {/* Default Currency & Preferred Unit Settings */}
        {settingsKeys.length > 0 && (
          <div className="space-y-4">
            {settingsKeys.map(key => renderSettingField(key, categorySettings[key]))}
          </div>
        )}

        {/* Currency Conversion Rates Section */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Currency Conversion Rates</h3>
              <p className="text-sm text-gray-600 mt-1">
                Manage USD to CAD conversion rates by year. Used for cost calculations when materials are entered in USD.
              </p>
            </div>
            <button
              onClick={handleAddConversionRate}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add Conversion Rate
            </button>
          </div>

          {conversionRatesLoading ? (
            <div className="text-center py-8">
              <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-400 mb-4" />
              <p className="text-gray-500">Loading conversion rates...</p>
            </div>
          ) : conversionRates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No conversion rates configured. Click "Add Conversion Rate" to add one.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">From</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">To</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {conversionRates
                    .sort((a, b) => b.year - a.year)
                    .map((rate) => (
                    <tr key={rate.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{rate.year}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rate.fromCurrency}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rate.toCurrency}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{rate.conversionRate}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          rate.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {rate.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditConversionRate(rate)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                            title="Edit conversion rate"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </button>
                          <button
                            onClick={() => handleDeleteConversionRate(rate.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                            title="Delete conversion rate"
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Dynamic configuration tab renderers
  const renderCoatingSystemsTab = () => {
    // Ensure coatingSystems is an array
    const systems = Array.isArray(coatingSystems) ? coatingSystems : [];
    
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Coating Systems Configuration</h3>
            <p className="text-sm text-gray-600">Manage coating system definitions and sequences</p>
          </div>
          <button
            onClick={() => {
              setEditingItem(null);
              setCoatingFormData({
                system_code: '',
                system_name: '',
                description: '',
                coat_sequence: ['prime', 'topcoat'],
                is_active: true
              });
              setShowCoatingModal(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Coating System
          </button>
        </div>

        {systems.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <FontAwesomeIcon icon={faSpinner} spin className="text-2xl text-gray-400 mb-4" />
            <p className="text-gray-500">Loading coating systems...</p>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coat Sequence</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {systems.map((system) => (
                <tr key={system.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{system.system_code}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{system.system_name}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex flex-wrap gap-1">
                      {(Array.isArray(system.coat_sequence) ? system.coat_sequence : JSON.parse(system.coat_sequence || '[]')).map((coat, index) => (
                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {coat}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      system.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {system.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          console.log('Edit button clicked for system:', system.system_code);
                          setEditingItem(system);
                          setCoatingFormData({
                            system_code: system.system_code,
                            system_name: system.system_name,
                            description: system.description || '',
                            coat_sequence: Array.isArray(system.coat_sequence) ? system.coat_sequence : JSON.parse(system.coat_sequence || '[]'),
                            is_active: system.is_active
                          });
                          setShowCoatingModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                        title="Edit coating system"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button
                        onClick={() => {
                          console.log('Delete button clicked for system:', system.system_code);
                          handleDeleteCoatingSystem(system.id);
                        }}
                        className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                        title="Delete coating system"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    );
  };

  const renderDegradationModelsTab = () => {
    // Ensure degradationParams is an array
    const params = Array.isArray(degradationParams) ? degradationParams : [];
    
    return (
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Degradation Parameters Configuration</h3>
          <p className="text-sm text-gray-600">Manage degradation formula parameters for different corrosion categories</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="fas fa-chart-line text-blue-600"></i>
            <h4 className="text-lg font-semibold text-blue-800">Degradation Formula: D(t) = A × e^(B × t)</h4>
          </div>
          <p className="text-sm text-blue-700">
            Where: A = coefficient, B = growth rate, t = years since last paint
          </p>
        </div>

        {params.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <div className="text-2xl text-gray-400 mb-4">⏳</div>
            <p className="text-gray-500">Loading degradation parameters...</p>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Corrosion Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coefficient A</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Growth Rate B</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Speed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sample (5 years)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {params.map((param) => (
                <tr key={param.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{param.corrosion_category}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{param.coefficient_a}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{param.growth_rate_b}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      param.degradation_speed === 'slowest' ? 'bg-green-100 text-green-800' :
                      param.degradation_speed === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      param.degradation_speed === 'faster' ? 'bg-orange-100 text-orange-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {param.degradation_speed}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400">🧮</span>
                      {(param.coefficient_a * Math.exp(param.growth_rate_b * 5)).toFixed(6)}%
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => {
                          console.log('Edit degradation param clicked for:', param.corrosion_category);
                          handleEditDegradationParam(param);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                        title="Edit degradation parameters"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    );
  };

  const renderTabContent = () => {
    // Handle special tabs
    if (activeTab === 'currency_units') {
      return renderCurrencyUnitsTab();
    } else if (activeTab === 'coating_systems') {
      return renderCoatingSystemsTab();
    }

    // Handle regular settings tabs
    const categorySettings = settings[activeTab] || {};
    const settingsKeys = Object.keys(categorySettings);
    
    if (settingsKeys.length === 0) {
      return (
        <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
          <i className="fas fa-cog text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-500">No settings available for this category</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {settingsKeys.map(key => renderSettingField(key, categorySettings[key]))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
        <p className="text-gray-600">Configure system-wide defaults and parameters for bridge maintenance budgeting</p>
      </div>
      
      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg border ${
          message.includes('Error') 
            ? 'bg-red-50 border-red-200 text-red-800' 
            : 'bg-green-50 border-green-200 text-green-800'
        }`}>
          <div className="flex items-center">
            <i className={`fas ${message.includes('Error') ? 'fa-exclamation-circle' : 'fa-check-circle'} mr-2`}></i>
            {message}
          </div>
        </div>
      )}
      
      {/* Tabs */}
      <div className="mb-8">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={`fas fa-${tab.icon}`}></i>
                <span>{tab.name}</span>
              </button>
            ))}
          </nav>
        </div>
      </div>
      
      {/* Content */}
      <div className="mb-8">
        {renderTabContent()}
      </div>
      
      {/* Actions */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2"></i>
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Currency Conversion Rate Modal */}
      {showConversionRateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingConversionRate ? 'Edit Conversion Rate' : 'Add Conversion Rate'}
              </h3>
              <button
                onClick={() => {
                  setShowConversionRateModal(false);
                  setEditingConversionRate(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Year *</label>
                <input
                  type="number"
                  value={conversionRateFormData.year}
                  onChange={(e) => setConversionRateFormData({ ...conversionRateFormData, year: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  min="2000"
                  max="2100"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">From Currency *</label>
                  <select
                    value={conversionRateFormData.from_currency}
                    onChange={(e) => setConversionRateFormData({ ...conversionRateFormData, from_currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="USD">USD</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">To Currency *</label>
                  <select
                    value={conversionRateFormData.to_currency}
                    onChange={(e) => setConversionRateFormData({ ...conversionRateFormData, to_currency: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="CAD">CAD</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conversion Rate *</label>
                <input
                  type="number"
                  step="0.0001"
                  value={conversionRateFormData.conversion_rate}
                  onChange={(e) => setConversionRateFormData({ ...conversionRateFormData, conversion_rate: parseFloat(e.target.value) })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                  placeholder="e.g., 1.35"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {conversionRateFormData.from_currency} 1 = {conversionRateFormData.to_currency} {conversionRateFormData.conversion_rate || '?'}
                </p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active_rate"
                  checked={conversionRateFormData.is_active}
                  onChange={(e) => setConversionRateFormData({ ...conversionRateFormData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active_rate" className="ml-2 block text-sm text-gray-900">Active</label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => {
                    setShowConversionRateModal(false);
                    setEditingConversionRate(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveConversionRate}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <i className="fas fa-save"></i>
                  {editingConversionRate ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Coating System Modal */}
      {showCoatingModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingItem ? 'Edit Coating System' : 'Add New Coating System'}
              </h3>
              <button
                onClick={() => setShowCoatingModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">System Code *</label>
                <input
                  type="text"
                  value={coatingFormData.system_code}
                  onChange={(e) => setCoatingFormData({ ...coatingFormData, system_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">System Name *</label>
                <input
                  type="text"
                  value={coatingFormData.system_name}
                  onChange={(e) => setCoatingFormData({ ...coatingFormData, system_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={coatingFormData.description}
                  onChange={(e) => setCoatingFormData({ ...coatingFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coat Sequence *</label>
                <div className="space-y-2">
                  {coatingFormData.coat_sequence.map((coat, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={coat}
                        onChange={(e) => {
                          const newSequence = [...coatingFormData.coat_sequence];
                          newSequence[index] = e.target.value;
                          setCoatingFormData({ ...coatingFormData, coat_sequence: newSequence });
                        }}
                        placeholder="Coat type (e.g., prime, stripe, midcoat, topcoat)"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      {coatingFormData.coat_sequence.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newSequence = coatingFormData.coat_sequence.filter((_, i) => i !== index);
                            setCoatingFormData({ ...coatingFormData, coat_sequence: newSequence });
                          }}
                          className="text-red-600 hover:text-red-800"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setCoatingFormData({ ...coatingFormData, coat_sequence: [...coatingFormData.coat_sequence, ''] });
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + Add Coat Type
                  </button>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={coatingFormData.is_active}
                  onChange={(e) => setCoatingFormData({ ...coatingFormData, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">Active</label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowCoatingModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCoatingSystem}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <i className="fas fa-save"></i>
                  {editingItem ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;